import type { Event } from '@/config/objects/events';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  RadioGroup,
  RadioGroupItem,
  toast,
  useApiClient,
} from '@startupquickstart/frontend';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { TriangleAlert } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

type SaveMode = 'create' | 'update';

export interface SaveAsTemplateDialogProps {
  event: Event;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Saves an event's structure back into the Game Template library — either as
 * a brand-new template or by overwriting the template the event was created
 * from (`event.templateId`). Modeled on CloneTemplateDialog.
 */
export function SaveAsTemplateDialog({ event, open, onOpenChange }: SaveAsTemplateDialogProps) {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const [mode, setMode] = useState<SaveMode>('create');
  const [name, setName] = useState('');

  const hasLinkedTemplate = !!event.templateId;

  useEffect(() => {
    if (open) {
      setMode('create');
      setName(`${event.name} Template`);
    }
  }, [open, event.name]);

  // Linked template details — only needed to preview the update target.
  const { data: linkedTemplateResp } = useQuery<{
    success: boolean;
    data: { id: string; name: string };
  }>({
    queryKey: ['Template', event.templateId],
    queryFn: () => apiClient.get(`/templates/${event.templateId}`),
    enabled: open && hasLinkedTemplate,
  });
  const linkedTemplate = linkedTemplateResp?.data;

  const saveMutation = useMutation({
    mutationFn: async (payload: { mode: SaveMode; name?: string }) => {
      return apiClient.post(`/events/${event.id}/save-as-template`, payload) as Promise<{
        success: boolean;
        data: { id: string; name: string };
      }>;
    },
    onSuccess: (response, payload) => {
      const template = response.data;
      queryClient.invalidateQueries({ queryKey: ['Template'] });
      queryClient.invalidateQueries({ queryKey: ['Template', template.id] });
      toast.success(
        payload.mode === 'update' ? 'Template updated from event' : 'Template created from event',
      );
      onOpenChange(false);
      navigate(`/templates/${template.id}/overview`);
    },
    onError: (err: Error) => {
      toast.error(err?.message || 'Failed to save template');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (mode === 'create') {
      saveMutation.mutate({ mode, name: name.trim() });
    } else {
      saveMutation.mutate({ mode });
    }
  };

  const submitDisabled = saveMutation.isPending || (mode === 'create' && !name.trim());

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Save as Template</DialogTitle>
            <DialogDescription>
              Capture this event&apos;s challenges, groups, and settings as a reusable game
              template.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <RadioGroup
              value={mode}
              onValueChange={(value) => setMode(value as SaveMode)}
              className="space-y-3"
            >
              <div className="flex items-start gap-2.5">
                <RadioGroupItem id="save-mode-create" value="create" className="mt-0.5" />
                <div className="flex-1 space-y-2">
                  <Label htmlFor="save-mode-create" className="text-sm font-medium cursor-pointer">
                    Create new template
                  </Label>
                  {mode === 'create' && (
                    <div className="space-y-2">
                      <Label htmlFor="save-template-name" className="sr-only">
                        Template Name
                      </Label>
                      <Input
                        id="save-template-name"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="Enter a name for the new template"
                        required
                      />
                    </div>
                  )}
                </div>
              </div>

              <div className="flex items-start gap-2.5">
                <RadioGroupItem
                  id="save-mode-update"
                  value="update"
                  disabled={!hasLinkedTemplate}
                  className="mt-0.5"
                />
                <div className="flex-1 space-y-1">
                  <Label
                    htmlFor="save-mode-update"
                    className={
                      hasLinkedTemplate
                        ? 'text-sm font-medium cursor-pointer'
                        : 'text-sm font-medium text-muted-foreground'
                    }
                  >
                    Update linked template
                  </Label>
                  {!hasLinkedTemplate && (
                    <p className="text-xs text-muted-foreground">
                      This event wasn&apos;t created from a template, so there is no linked
                      template to update.
                    </p>
                  )}
                  {hasLinkedTemplate && (
                    <p className="text-xs text-muted-foreground">
                      Overwrites{' '}
                      <span className="font-medium text-foreground">
                        {linkedTemplate?.name ?? 'the linked template'}
                      </span>{' '}
                      with this event&apos;s current structure.
                    </p>
                  )}
                </div>
              </div>
            </RadioGroup>

            {mode === 'update' && hasLinkedTemplate && (
              <div className="flex gap-2.5 rounded-lg border border-destructive/30 bg-destructive/10 p-3">
                <TriangleAlert className="h-4 w-4 text-destructive mt-0.5 shrink-0" />
                <p className="text-sm text-destructive">
                  The structure of{' '}
                  <span className="font-medium">{linkedTemplate?.name ?? 'the linked template'}</span>{' '}
                  — its challenges, groups, and settings — will be replaced by this event&apos;s.
                  This cannot be undone.
                </p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={saveMutation.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={submitDisabled}>
              {saveMutation.isPending
                ? 'Saving...'
                : mode === 'update'
                  ? 'Update Template'
                  : 'Create Template'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
